import styled, { css } from 'styled-components';
import { Typography } from '@mui/material';

import colors from '../../../../../chat/styles/colors';
import Tag from '../../../../../chat/components/Tag';

const PreviewContainer = styled.button`
  width: 100%;
  display: flex;
  flex-wrap: nowrap;
  padding: 10px;
  align-items: stretch;
  border: none;
  border-bottom: 0px solid ${colors.shades.secondary5};
  background-color: ${colors.base.alpha};
  cursor: pointer;

  ${({ $isActive }) => $isActive && css`
    background-color: ${colors.shades.secondary5};
    border-bottom: 1px solid ${colors.shades.secondary4};
  `}
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-basis: 100%;
  justify-content: space-evenly;
  overflow: hidden;
  min-height: 40px;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-basis: 100%;
  align-items: flex-start;
  justify-content: space-between;
  overflow: hidden;
  flex-grow: 1;
`;

const LeadName = styled.span`
  font-size: 16px;
  white-space: nowrap;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  text-align: left;
  font-weight: 500;
  line-height: 18px;
`;

const MessagePreview = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  min-height: 10px;
  font-size: 12px;
  font-weight: 400;
  line-height: 12px;
  text-align: left;
  color: ${colors.shades.gamma6};

  ${({ $isComment }) => $isComment && css`
    font-style: italic;
  `}
`;

const TimeStamp = styled.span`
  flex-shrink: 0;
  font-size: 10px;
  color: ${({ $isActive }) => ($isActive ? colors.shades.gamma10 : colors.shades.gamma6)};
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 0;
  flex-shrink: 0;
  justify-content: flex-end;
  align-items: center;
`;

/*
  Tags live in a single horizontal row. Previously each StyledTag was its own flex item in
  TextContainer's column, so a second tag added a whole stacked line (+35px) and the list
  developed ragged row heights.

  Tags keep their natural width (`Tag` sets `white-space: nowrap`, and shrinking them would
  clip the claim name mid-word), so `flex-wrap` is what handles the case where a long claim
  name plus the AI SDR label exceed the sidebar: they drop to a second line and the row grows,
  as it did before. That bounds the problem rather than removing it. A single tag wider than the
  sidebar still overflows and is clipped by TextContainer's `overflow: hidden`, unchanged from
  before.
*/
const TagRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin: 8px 0;
  max-width: 100%;
`;

const StyledTag = styled(Tag)`
  margin: 0;
  flex-shrink: 0;
`;

const LastContactedInfo = styled(Typography)`
  font-size: 12px;
  font-weight: 500;
  line-height: 12px;
  text-align: left;
  margin-top: 16px;
  color: ${colors.shades.secondary3};
`;

/*
  `secondary2` is the palette's link colour, which is the right token for a mention, and against
  white it reads better than the blue it replaced (5.92 vs 3.35 contrast). What the Avida retheme
  cost is hue separation: the mention and the surrounding preview text (`gamma6`) are now both
  greens, so the mention no longer reads as a distinct thing at a glance. Weight restores that
  without spending another palette colour, and works where a hue change would not - the preview
  runs italic at 400, so 600 is legible as emphasis rather than as a different shade of grey.
*/
const CommentMentionHighlight = styled.span`
  color: ${colors.shades.secondary2};
  font-weight: 600;
`;

export {
  PreviewContainer,
  ContentContainer,
  TextContainer,
  LeadName,
  MessagePreview,
  TimeStamp,
  InfoContainer,
  StyledTag,
  TagRow,
  CommentMentionHighlight,
  LastContactedInfo,
};
